import { SHContainer, SHStack } from "@components/design-systems";
import { usePlatformAnalysis } from "@hooks/usePlatformAnalysis";
import { CollapseSubGroup } from "@layouts/platform-analysis/collapse-sub-group";
import { GroupSwiperCarousel } from "@layouts/platform-analysis/group-swiper-carousel";
import { TransferSelectFeatureList } from "@layouts/review/transfer-select-feature-list";
import { StepCompProps, StepCompRef } from "@layouts/stepper";
import { StepName } from "@layouts/stepper/step-name";
import { RootState } from "@redux/store";
import { first, isEmpty } from "lodash";
import {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { useEffectOnce, useToggle } from "react-use";
import { ConfirmSelectAllPlatformsDialog } from "../../../components/dialogs/confirm-select-all";
import { AnalysisFeatureSelectionSkeleton } from "../../../components/skeletons/feature-selection";
import {
  getFeatureSelectDeselectResult,
  isEqualFeatureSelectionsWithStoredData,
} from "./utils";

export interface AnalysisFeatureSelectionProps extends StepCompProps {}
export const FeatureSelectionStep: ForwardRefRenderFunction<
  StepCompRef,
  StepCompProps
> = ({ stepNumber }, ref) => {
  const { analysisId } = useParams<{ analysisId: string }>();
  const storedIsLoading = useSelector(
    (state: RootState) => state.platformAnalysis.ui.isLoading,
  );
  const isDirty = useSelector(
    (state: RootState) => state.platformAnalysis?.ui?.isDirty,
  );
  const collapsedSubGroupIds = useSelector(
    (state: RootState) => state.platformAnalysis?.ui?.collapsedSubGroupIds,
  );
  const storeSelectionsSection = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.originalFeatureSelections?.sections,
  );
  const storedFeatureSelections = useSelector(
    (state: RootState) => state.platformAnalysis?.featureSelections,
  );
  const isReview = useSelector(
    (state: RootState) => state.platformAnalysis?.platformAnalysis?.isReview,
  );

  const {
    loadFeatureSelections,
    updateFeatureSelections,
    setIsDirty,
    setIsValid,
    setFeatureSelections,
  } = usePlatformAnalysis();

  useImperativeHandle(ref, () => ({
    onChangeStep: async () => {
      return await handleOnSubmit();
    },
    onChangeOtherStep: async () => {
      setIsDirty(false);
      return await handleOnSubmit();
    },
    onSubmit: async () => {
      setIsDirty(false);
      return await handleOnSubmit();
    },
  }));

  // #region States & Variables
  const [isLoadingLocal, toggleIsLoadingLocal] = useToggle(true);
  const [showConfirmSelectAllDialog, toggleConfirmSelectAllDialogShow] =
    useToggle(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>("");

  const selections = useMemo(
    () => storedFeatureSelections?.sections,
    [storedFeatureSelections],
  );

  const selectedSection = useMemo(() => {
    return (
      selections?.find((section) => section?.isSelected) ?? first(selections)
    );
  }, [selections]);

  const hasSelectedFeature = useMemo(() => {
    if (!selections || selections.length === 0) return false;
    return selections.some((section) =>
      section.subSections?.some((sub) =>
        sub.features?.some((feature) => !!feature?.isSelected),
      ),
    );
  }, [selections]);

  const selectedSubSections = useMemo(() => {
    return selectedSection?.subSections;
  }, [selectedSection]);
  // #endregion States & Variables

  // #region Handlers
  const handleOnSubmit = async () => {
    const hasRecalculateData = !!storedFeatureSelections?.hasRecalculate;
    if (!isDirty && !hasRecalculateData) return true;
    if (!analysisId || !storedFeatureSelections) return false;

    const isSuccess = await updateFeatureSelections({
      platformAnalysisId: analysisId,
      featureSelections: storedFeatureSelections,
    });
    setIsDirty(!isSuccess);
    return isSuccess;
  };

  const loadSelections = async (analysisId?: string) => {
    if (!analysisId || analysisId === "new") return;
    const { sections = [] } =
      (await loadFeatureSelections({
        platformAnalysisId: analysisId,
      })) || {};
    setFeatureSelections(sections);
  };

  const handleOnSelectGroup = (groupId?: string) => {
    const newGroups = selections?.map((group) => {
      return {
        ...group,
        isSelected: group.id === groupId,
      };
    });
    setFeatureSelections(newGroups ?? []);
  };

  const handleOnSelectDeselectFeature = (
    subGroupId?: string,
    featureId?: string,
    isDeselect?: boolean,
  ) => {
    if (!featureId || !selectedSection?.id || !subGroupId) return;
    const newGroups = getFeatureSelectDeselectResult({
      isDeselect,
      originalSelections: selections,
      targetSectionId: selectedSection.id,
      targetSubSectionId: subGroupId,
      targetFeatureId: featureId,
    });
    setFeatureSelections(newGroups);
  };

  const handleOnSelectDeselectSubSection = (
    subGroupId?: string,
    isDeselect?: boolean,
  ) => {
    if (!subGroupId || !selectedSection?.id) return;
    const newGroups = getFeatureSelectDeselectResult({
      isDeselect,
      originalSelections: selections,
      targetSectionId: selectedSection.id,
      targetSubSectionId: subGroupId,
    });
    setFeatureSelections(newGroups ?? []);
  };

  const handleOnSelectDeselectAllGroupFeatures = (groupId?: string) => {
    if (!groupId) return;
    setSelectedGroupId(groupId);

    const { totalSelectedFeature = 0, totalFeature = 0 } =
      selectedSection || {};
    const hasPartialSelection =
      totalSelectedFeature > 0 && totalSelectedFeature < totalFeature;
    const hasFullSelection = totalSelectedFeature === totalFeature;

    if (hasPartialSelection) {
      toggleConfirmSelectAllDialogShow(true);
      return;
    }

    handleOnChangeSelectedAllGroupFeature(!hasFullSelection, groupId);
  };

  const handleOnChangeSelectedAllGroupFeature = (
    isSelected?: boolean,
    groupId?: string,
  ) => {
    if (!groupId) return;
    const newGroups = getFeatureSelectDeselectResult({
      isDeselect: !isSelected,
      originalSelections: selections,
      targetSectionId: groupId,
    });
    setFeatureSelections(newGroups);
  };

  const handleOnSelectDeselectEssential = (
    subGroupId?: string,
    featureId?: string,
  ) => {
    if (!featureId || !selectedSection?.id || !subGroupId) return;
    const newGroups = getFeatureSelectDeselectResult({
      isDeselect: false,
      isUpdateEssential: true,
      originalSelections: selections,
      targetSectionId: selectedSection.id,
      targetSubSectionId: subGroupId,
      targetFeatureId: featureId,
    });
    setFeatureSelections(newGroups ?? []);
  };
  // #endregion Handlers

  // #region Renderers
  const renderGroupCarousel = () => {
    if (isEmpty(selections)) return null;
    return (
      <GroupSwiperCarousel
        groups={selections}
        handleOnSelect={handleOnSelectGroup}
        handleOnSelectDeselectAll={handleOnSelectDeselectAllGroupFeatures}
      />
    );
  };

  const renderSubGroupCollapse = () => {
    if (isEmpty(selectedSubSections)) return null;
    return selectedSubSections?.map((subGroup, index) => (
      <CollapseSubGroup
        key={index}
        subSection={subGroup}
        isExpanded={!collapsedSubGroupIds?.find((id) => id === subGroup.id)}
      >
        <TransferSelectFeatureList
          marginY={3}
          subGroupId={subGroup.id}
          features={subGroup?.features}
          handleOnSelectDeselect={handleOnSelectDeselectFeature}
          handleOnSelectDeselectEssential={handleOnSelectDeselectEssential}
          handleOnSelectDeselectAll={(subGroupId, _, isDeselect) =>
            handleOnSelectDeselectSubSection(subGroupId, isDeselect)
          }
        />
      </CollapseSubGroup>
    ));
  };
  // #endregion Renderers

  // #region Effects
  useEffect(() => {
    loadSelections(analysisId).then(() => {
      toggleIsLoadingLocal(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  useEffectOnce(() => {
    return () => {
      setIsValid(true);
      setIsDirty(false);
    };
  });

  useEffect(() => {
    setIsValid(hasSelectedFeature);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSelectedFeature]);

  useEffect(() => {
    const isEqualStoreData = isEqualFeatureSelectionsWithStoredData(
      storeSelectionsSection,
      selections,
    );
    setIsDirty(!isEqualStoreData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);
  // #endregion

  if (isLoadingLocal || storedIsLoading)
    return <AnalysisFeatureSelectionSkeleton />;

  return (
    <SHContainer
      sx={{
        px: { xs: "16px", lg: 0 },
        py: "25px",
        display: isLoadingLocal ? "none" : "block",
      }}
    >
      <StepName
        name={
          isReview
            ? "What features are important for your client?"
            : "Feature selection"
        }
        stepNumber={stepNumber}
      />
      <SHStack marginY={3}>
        {renderGroupCarousel()}
        {renderSubGroupCollapse()}

        <ConfirmSelectAllPlatformsDialog
          open={showConfirmSelectAllDialog}
          totalSelectedFeature={selectedSection?.totalSelectedFeature}
          onCancel={() => toggleConfirmSelectAllDialogShow(false)}
          onSelectAll={() => {
            handleOnChangeSelectedAllGroupFeature(true, selectedGroupId);
            toggleConfirmSelectAllDialogShow(false);
          }}
        />
      </SHStack>
    </SHContainer>
  );
};
